import { useEffect, useState } from "react";

const TICK_MS = 60000; // live update every minute

function breakdown(msRemaining) {
    if (msRemaining <= 0) return { done: true, days: 0, hours: 0, minutes: 0, text: "Less than 1 Minute" };
    const totalMinutes = Math.ceil(msRemaining / (60 * 1000));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    let text;
    if (days > 0) {
        text = `${days} Day${days === 1 ? "" : "s"} ${hours} Hour${hours === 1 ? "" : "s"}`;
    } else if (hours > 0) {
        text = `${hours} Hour${hours === 1 ? "" : "s"}`;
    } else {
        text = `${minutes} Minute${minutes === 1 ? "" : "s"}`;
    }
    return { done: false, days, hours, minutes, text };
}

// Formats a lock/unlock timestamp as the weekday it falls on ("Tuesday")
// rather than a duration ("2 Days 6 Hours") — the day-by-day schedule always
// unlocks at a learner's own local midnight, so the weekday name alone is
// enough to answer "when does this open." Once more than 6 days out (rare —
// only really possible via an admin override), the date is appended so it
// doesn't read as "this coming Tuesday" when it's actually weeks away.
export function formatUnlockDay(targetIso) {
    if (!targetIso) return "";
    const target = new Date(targetIso);
    if (Number.isNaN(target.getTime())) return "";
    const now = new Date();
    const diffDays = Math.floor((target - now) / (24 * 60 * 60 * 1000));
    const weekday = target.toLocaleDateString(undefined, { weekday: "long" });
    if (diffDays > 6) {
        const dateStr = target.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        return `${weekday} (${dateStr})`;
    }
    return weekday;
}

// Ticks a countdown to `targetIso` once a minute. Pass `active: false` to
// pause ticking (e.g. while a dialog/card isn't visible). Format: "6 Days 18
// Hours" while more than a day remains, "3 Hours" once under a day, "25
// Minutes" once under an hour — never a bare hour count once past 24h. When
// the countdown reaches zero, `done` flips true so the caller can refresh.
export function useCountdown(targetIso, active = true) {
    const target = targetIso ? new Date(targetIso).getTime() : null;
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!active || !target) return;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), TICK_MS);
        return () => clearInterval(id);
    }, [active, target]);

    if (!target) return { done: true, days: 0, hours: 0, minutes: 0, text: "Less than 1 Minute" };
    return breakdown(target - now);
}
